import { FC, useEffect, useState } from "react";
import { Box, Typography, Checkbox } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { PositionInfo, AIPositionInfo } from "../InterviewAssistant";
import colors from "../../../shared/constants/color";
import { textSM, textXS } from "../../../shared/constants";
import { Button } from "../../../components";
import { WorkingModelTag } from "../components/shared/WorkingModelTag";
import ModalBase from "../../../components/ui/ModalBase";
import axios from "axios";
import { SERVICEURL } from "../../../appconfig";
import { useToast } from "../../../shared/hooks/useToast";
import { ProjectStatusParameterGuids } from "../../../constants/parameterGuids";
import { Button as MuiButton } from "@mui/material";
import { useWiserTheme } from "../../../hooks/useWiserTheme";
import { getClearFiltersBtnSx, getModalDataGridSx } from "../../../shared/theme/wiserThemeHelpers";
import { modalMaxHeightSx, addPositionGridWrapSx, addPositionTechGroupCellSx } from "../style";

interface IProps {
    show: boolean;
    onAdd: (selectedPositions: PositionInfo[]) => void;
    onClose: () => void;
    aiRecommendedPositions: AIPositionInfo[] | null;
}

const AddPositionModal: FC<IProps> = ({
    show,
    onAdd,
    onClose,
    aiRecommendedPositions,
}) => {
    const { darkModeActive, tokens } = useWiserTheme();
    const [loading, setLoading] = useState(false);
    const [abortController, setAbortController] = useState<AbortController | null>(null);
    const [positions, setPositions] = useState<PositionInfo[]>([]);
    const [selectedPositionIds, setSelectedPositionIds] = useState<string[]>([]);
    const existingPositionIds = aiRecommendedPositions?.map(p => p.positionId) || [];
    const mytoken = localStorage.getItem("token");
    const { showToast } = useToast();

    useEffect(() => {
        if (show) {
            setPositions([]);
            setSelectedPositionIds([]);
            fetchOpenPositions();
        }
    }, [show]);

    const fetchOpenPositions = async () => {
        if (abortController) {
            abortController.abort();
        }
        const newAbortController = new AbortController();
        setAbortController(newAbortController);
        setLoading(true);
        try {
            const response = await axios.get(
                `${SERVICEURL}api/Position/GetPositionList`,
                {
                    params: {
                        page: 1,
                        pageSize: 10000,
                        statusId: ProjectStatusParameterGuids.aktif,
                    },
                    headers: {
                        'accept': 'text/plain',
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${mytoken}`
                    },
                    cancelToken: new axios.CancelToken((cancel) => {
                        newAbortController.signal.addEventListener('abort', () => {
                            cancel('Request cancelled');
                        });
                    })
                }
            );

            const code = response.data.code;
            const message = response.data.message;
            const responseData = response.data.data;

            if (code !== 200 || !responseData) {
                showToast(message, "error");
                setPositions([]);
                return;
            }

            const positionsList = (responseData.items || []).map((item: any) => ({
                ...item,
                id: String(item.id),
            })) as PositionInfo[];
            setPositions(positionsList);
        } catch (error: any) {
            if (error.name === 'AbortError' || error.name === 'CanceledError' || axios.isCancel(error)) {
                return;
            }

            showToast(
                error.response?.data?.message ||
                "Pozisyonlar yüklenirken hata oluştu.",
                "error"
            );
            setPositions([]);
        } finally {
            setLoading(false);
        }
    };

    const handleCheckboxChange = (positionId: string) => {
        if (existingPositionIds.includes(positionId)) {
            return;
        }
        setSelectedPositionIds(prev => {
            if (prev.includes(positionId)) {
                return prev.filter(id => id !== positionId);
            } else {
                return [...prev, positionId];
            }
        });
    };

    const handleAdd = () => {
        const newSelectedPositions = positions.filter(
            p => selectedPositionIds.includes(String(p.id)) && !existingPositionIds.includes(String(p.id))
        );
        onAdd(newSelectedPositions);
    };

    const handleClose = () => {
        if (abortController) {
            abortController.abort();
        }
        setAbortController(null);
        onClose();
    };

    const columns: GridColDef[] = [
        {
            field: "select",
            headerName: "",
            width: 60,
            sortable: false,
            disableColumnMenu: true,
            renderCell: (params) => {
                const positionId = String(params.row.id);
                const isSelected = selectedPositionIds.includes(positionId);
                const isExisting = existingPositionIds.includes(positionId);
                return (
                    <Checkbox
                        checked={isSelected || isExisting}
                        onChange={() => handleCheckboxChange(positionId)}
                        disabled={isExisting}
                        size="small"
                    />
                );
            },
        },
        {
            field: "positionParameterName",
            headerName: "Teknik Grup",
            flex: 2,
            minWidth: 200,
            sortable: true,
            renderCell: (params) => {
                const position = params.row as PositionInfo;
                return (
                    <Box sx={addPositionTechGroupCellSx}>
                        <Typography
                            sx={textSM(
                                500,
                                darkModeActive ? tokens.textPrimary : colors.neutral.N800,
                            )}
                        >
                            {position.positionParameterName || ""}
                        </Typography>
                        <WorkingModelTag workingModel={position.workingModelName || ""} />
                    </Box>
                );
            },
        },
        {
            field: "clientName",
            headerName: "Müşteri",
            flex: 1.5,
            minWidth: 120,
            sortable: true,
            renderCell: (params) => (
                <Typography
                    sx={textXS(
                        400,
                        darkModeActive ? tokens.textSecondary : colors.neutral.N500,
                    )}
                >
                    {params.row.clientName || ""}
                </Typography>
            ),
        },
        {
            field: "teamName",
            headerName: "Ekip",
            flex: 1.5,
            minWidth: 120,
            sortable: true,
            renderCell: (params) => (
                <Typography
                    sx={textXS(
                        400,
                        darkModeActive ? tokens.textSecondary : colors.neutral.N500,
                    )}
                >
                    {params.row.teamName || ""}
                </Typography>
            ),
        },
    ];

    return (
        <ModalBase
            open={show}
            onClose={handleClose}
            title="Pozisyon Ekle"
            maxWidth="md"
            actions={
                <>
                    <MuiButton
                        variant="outlined"
                        onClick={handleClose}
                        sx={getClearFiltersBtnSx(darkModeActive, {
                            borderColor: darkModeActive ? tokens.border : undefined,
                        })}
                    >
                        İptal
                    </MuiButton>
                    <Button
                        color={colors.deepBlue}
                        variant="contained"
                        onClick={handleAdd}
                        disabled={selectedPositionIds.length === 0}
                    >
                        Listeye Ekle
                    </Button>
                </>
            }
            sx={modalMaxHeightSx}
        >
            <Box sx={addPositionGridWrapSx}>
                <DataGrid
                    rows={positions}
                    columns={columns}
                    getRowId={(row) => String(row.id)}
                    disableColumnFilter
                    disableColumnSelector
                    disableDensitySelector
                    selectionModel={[]}
                    loading={loading}
                    autoHeight
                    sx={getModalDataGridSx(darkModeActive) as any}
                />
            </Box>
        </ModalBase>
    );
};

export default AddPositionModal;
